import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  CheckCircle, 
  ArrowRight, 
  Phone, 
  MessageCircle,
  FileText,
  Clock,
  Shield,
  Users
} from 'lucide-react';
import FAQAccordion from '../../components/FAQAccordion';
import { ServiceSchema, FAQSchema, BreadcrumbSchema } from '../../components/SchemaMarkup';
import { services, faqData, serviceFAQ, cities, businessData } from '../../data/seoData';

const UnfallgutachtenPage = () => {
  const service = services.unfallgutachten;
  
  // FAQ Daten für diese Seite (8 Fragen)
  const pageFAQs = faqData.filter(faq => serviceFAQ.includes(faq.id));

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${businessData.whatsapp}`, '_blank');
  };

  const handleCall = () => {
    window.location.href = `tel:$Jetzt anrufen`;
  };

  const processSteps = [
    {
      number: 1,
      title: 'Kontaktaufnahme',
      description: 'Sie kontaktieren uns per WhatsApp, Telefon oder Formular'
    },
    {
      number: 2,
      title: 'Terminvereinbarung',
      description: 'Wir vereinbaren einen schnellen Vor-Ort-Termin'
    },
    {
      number: 3,
      title: 'Begutachtung',
      description: 'Detaillierte Schadensdokumentation mit Fotos vor Ort'
    },
    {
      number: 4,
      title: 'Gutachten',
      description: 'Sie erhalten Ihr rechtssicheres Unfallgutachten'
    }
  ];

  const benefits = [
    {
      icon: Shield,
      title: 'Unabhängig & objektiv',
      description: 'Neutrale Bewertung ohne Interessenkonflikte'
    },
    {
      icon: FileText,
      title: 'Rechtssicher',
      description: 'Gutachten verwendbar für Versicherung und Anwalt'
    },
    {
      icon: Clock,
      title: 'Schnell vor Ort',
      description: 'Kurzfristige Termine im Rhein-Main-Gebiet'
    },
    {
      icon: Users,
      title: 'Für Privat & Gewerbe',
      description: 'Unfallgutachten für alle Fahrzeugtypen'
    }
  ];

  return (
    <div style={{ background: '#FFFFFF', position: 'relative' }}>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Unfallgutachten Rhein-Main | Crash Check Kfz Gutachter</title>
        <meta name="description" content="Unfallgutachten im Rhein-Main-Gebiet. Schneller Vor-Ort-Service in Frankfurt, Wiesbaden, Mainz. Rechtssichere Dokumentation für Versicherung & Anwalt." />
        <meta name="keywords" content="Unfallgutachten, Kfz Gutachter, Rhein-Main, Frankfurt, Wiesbaden, Mainz" />
        <link rel="canonical" href={`${businessData.url}${service.url}`} />
      </Helmet>

      {/* Schema Markup */}
      <ServiceSchema service={service} />
      <FAQSchema faqs={pageFAQs} />
      <BreadcrumbSchema items={[
        { name: 'Start', url: '/' },
        { name: 'Leistungen', url: '/dienstleistungen' },
        { name: 'Unfallgutachten', url: service.url }
      ]} />

      {/* Hero Section */}
      <section style={{ 
        paddingTop: '140px', 
        paddingBottom: '80px',
        background: 'linear-gradient(180deg, #F0F4F8 0%, #FFFFFF 100%)'
      }}>
        <div className="container mx-auto px-4" style={{ maxWidth: '1200px' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
            {/* Breadcrumb */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              gap: '8px',
              marginBottom: '24px',
              fontSize: '14px',
              color: '#718096'
            }}>
              <Link to="/" style={{ color: '#0891B2', textDecoration: 'none' }}>Start</Link>
              <span>/</span>
              <Link to="/dienstleistungen" style={{ color: '#0891B2', textDecoration: 'none' }}>Leistungen</Link>
              <span>/</span>
              <span>Unfallgutachten</span>
            </div>

            <h1 style={{
              fontSize: 'clamp(36px, 6vw, 56px)',
              fontWeight: '700',
              lineHeight: '1.2',
              color: '#1a202c',
              marginBottom: '24px'
            }}>
              Unfallgutachten Rhein-Main
            </h1>
            
            <p style={{
              fontSize: '20px',
              lineHeight: '1.7',
              color: '#4a5568',
              marginBottom: '40px'
            }}>
              Rechtssichere Dokumentation nach Unfällen. Schnell vor Ort in Frankfurt, Wiesbaden, Mainz und Umgebung.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={handleWhatsApp} className="btn-primary" style={{ minHeight: '56px' }}>
                <MessageCircle size={20} />
                WhatsApp Soforthilfe
              </button>
              <button onClick={handleCall} className="btn-secondary" style={{ minHeight: '56px' }}>
                <Phone size={20} />
                Jetzt anrufen
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Für wen? */}
      <section className="pad-large" style={{ background: '#FFFFFF' }}>
        <div className="container mx-auto px-4" style={{ maxWidth: '1200px' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: 'clamp(28px, 4vw, 40px)',
              fontWeight: '700',
              color: '#1a202c',
              marginBottom: '24px'
            }}>
              Für wen ist ein Unfallgutachten sinnvoll?
            </h2>
            <div style={{
              fontSize: '18px',
              lineHeight: '1.8',
              color: '#4a5568'
            }}>
              <p style={{ marginBottom: '20px' }}>
                Ein Unfallgutachten ist besonders wichtig, wenn Sie nach einem Verkehrsunfall eine <strong>rechtssichere Dokumentation</strong> für die Schadensregulierung benötigen.
              </p>
              <div className="glass-card" style={{ padding: '32px', marginBottom: '24px' }}>
                <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1a202c', marginBottom: '16px' }}>
                  Unfallgutachten für Privatkunden
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {[
                    'Nach einem unverschuldeten Unfall',
                    'Bei größeren Schäden (ab ca. 750€)',
                    'Wenn Wertminderung geltend gemacht werden soll',
                    'Bei Unklarheiten zur Schadenshöhe'
                  ].map((item, index) => (
                    <li key={index} style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      gap: '12px',
                      marginBottom: '12px',
                      fontSize: '16px',
                      color: '#4a5568'
                    }}>
                      <CheckCircle size={20} style={{ color: '#0891B2', marginTop: '2px', flexShrink: 0 }} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="glass-card" style={{ padding: '32px' }}>
                <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1a202c', marginBottom: '16px' }}>
                  Unfallgutachten für Gewerbekunden
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {[
                    'Fuhrparkfahrzeuge nach Unfällen',
                    'Transporterschäden',
                    'Nutzfahrzeuge aller Art',
                    'Schnelle Abwicklung für Betriebsablauf'
                  ].map((item, index) => (
                    <li key={index} style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      gap: '12px',
                      marginBottom: '12px',
                      fontSize: '16px',
                      color: '#4a5568'
                    }}>
                      <CheckCircle size={20} style={{ color: '#0891B2', marginTop: '2px', flexShrink: 0 }} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ablauf */}
      <section className="pad-large" style={{ background: '#F8F9FA' }}>
        <div className="container mx-auto px-4" style={{ maxWidth: '1200px' }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 style={{
              fontSize: 'clamp(28px, 4vw, 40px)',
              fontWeight: '700',
              color: '#1a202c',
              marginBottom: '16px'
            }}>
              So läuft ein Unfallgutachten ab
            </h2>
            <p style={{ fontSize: '18px', color: '#4a5568' }}>
              Einfach, schnell und transparent
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div key={step.number} style={{ textAlign: 'center' }}>
                <div style={{
                  width: '72px',
                  height: '72px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(8, 145, 178, 0.15) 0%, rgba(6, 182, 212, 0.1) 100%)',
                  border: '2px solid rgba(8, 145, 178, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '28px',
                  fontWeight: '800',
                  color: '#0891B2',
                  margin: '0 auto 20px'
                }}>
                  {step.number}
                </div>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#1a202c',
                  marginBottom: '12px'
                }}>
                  {step.title}
                </h3>
                <p style={{
                  fontSize: '15px',
                  color: '#4a5568',
                  lineHeight: '1.6'
                }}>
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vorteile */}
      <section className="pad-large" style={{ background: '#FFFFFF' }}>
        <div className="container mx-auto px-4" style={{ maxWidth: '1200px' }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 style={{
              fontSize: 'clamp(28px, 4vw, 40px)',
              fontWeight: '700',
              color: '#1a202c',
              marginBottom: '16px'
            }}>
              Warum ein unabhängiger Gutachter?
            </h2>
            <p style={{ fontSize: '18px', color: '#4a5568', maxWidth: '700px', margin: '0 auto' }}>
              Als unabhängiger Kfz Gutachter arbeiten wir neutral und objektiv – ohne Interessenkonflikte
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="glass-card" style={{ textAlign: 'center', padding: '32px' }}>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, rgba(8, 145, 178, 0.15) 0%, rgba(6, 182, 212, 0.1) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px'
                  }}>
                    <Icon size={32} style={{ color: '#0891B2' }} />
                  </div>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: '#1a202c',
                    marginBottom: '12px'
                  }}>
                    {benefit.title}
                  </h3>
                  <p style={{
                    fontSize: '15px',
                    color: '#4a5568',
                    lineHeight: '1.6'
                  }}>
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Einsatzgebiet */}
      <section className="pad-large" style={{ background: '#F8F9FA' }}>
        <div className="container mx-auto px-4" style={{ maxWidth: '1200px' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
            <h2 style={{
              fontSize: 'clamp(28px, 4vw, 40px)',
              fontWeight: '700',
              color: '#1a202c',
              marginBottom: '24px'
            }}>
              Unfallgutachten im Rhein-Main-Gebiet
            </h2>
            <p style={{
              fontSize: '18px',
              lineHeight: '1.8',
              color: '#4a5568',
              marginBottom: '32px'
            }}>
              Wir sind schnell vor Ort in den wichtigsten Städten der Region:
            </p>
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '16px', 
              justifyContent: 'center',
              marginBottom: '40px'
            }}>
              {Object.values(cities).map((city) => (
                <Link
                  key={city.slug}
                  to={city.url}
                  className="glass-card"
                  style={{
                    padding: '16px 24px',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(8, 145, 178, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '';
                  }}
                >
                  <span style={{ fontSize: '16px', fontWeight: '600', color: '#1a202c' }}>
                    {city.name}
                  </span>
                  <ArrowRight size={16} style={{ color: '#0891B2' }} />
                </Link>
              ))}
            </div>
            <p style={{ fontSize: '16px', color: '#718096' }}>
              Auch in weiteren Städten und Gemeinden im Rhein-Main-Gebiet im Einsatz
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="pad-large" style={{ background: '#FFFFFF' }}>
        <div className="container mx-auto px-4" style={{ maxWidth: '900px' }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 style={{
              fontSize: 'clamp(28px, 4vw, 40px)',
              fontWeight: '700',
              color: '#1a202c',
              marginBottom: '16px'
            }}>
              Häufig gestellte Fragen
            </h2>
            <p style={{ fontSize: '18px', color: '#4a5568' }}>
              Wichtige Antworten zum Unfallgutachten
            </p>
          </div>

          <FAQAccordion faqs={pageFAQs} />

          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            <Link to="/faq" style={{
              color: '#0891B2',
              fontSize: '16px',
              fontWeight: '600',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              Alle Fragen & Antworten
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="pad-large" style={{ background: '#F0F4F8' }}>
        <div className="container mx-auto px-4" style={{ maxWidth: '900px' }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(8, 145, 178, 0.1) 0%, rgba(6, 182, 212, 0.05) 100%)',
            border: '1px solid rgba(8, 145, 178, 0.2)',
            borderRadius: '24px',
            padding: '60px 40px',
            textAlign: 'center'
          }}>
            <h2 style={{
              fontSize: 'clamp(28px, 4vw, 40px)',
              fontWeight: '700',
              color: '#1a202c',
              marginBottom: '20px'
            }}>
              Unfall gehabt? <br/>Jetzt Gutachten anfragen
            </h2>
            <p style={{
              fontSize: '18px',
              color: '#4a5568',
              lineHeight: '1.7',
              marginBottom: '40px',
              maxWidth: '600px',
              margin: '0 auto 40px'
            }}>
              Kontaktieren Sie uns per WhatsApp, Telefon oder nutzen Sie unser Kontaktformular.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" style={{ marginBottom: '32px' }}>
              <button onClick={handleWhatsApp} className="btn-primary" style={{ minHeight: '56px' }}>
                <MessageCircle size={20} />
                WhatsApp Soforthilfe
              </button>
              <button onClick={handleCall} className="btn-secondary" style={{ minHeight: '56px' }}>
                <Phone size={20} />
                Anrufen
              </button>
            </div>
            <Link to="/kontakt" style={{
              color: '#0891B2',
              fontSize: '16px',
              fontWeight: '600',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              Zum Kontaktformular
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UnfallgutachtenPage;
